import { ONLINE_STATUS } from "verdi-common";
import { Device } from "verdi-common/schemas/device/device";
import type { NetworkTestPeriod } from "verdi-common/schemas/device/getters";
import { LoraNetworkProfileConfig } from "verdi-common/schemas/device/subSchemas/loraNetworkProfileConfig";
import { LoraNetworkProfile } from "verdi-common/schemas/loraNetworkProfile/loraNetworkProfile";
import { findUtilities } from "verdi-common/schemas/utils/findUtilities";
import { isValidNum } from "verdi-common/utils/mathUtils";
import type DeviceMapEntity from "../../../map-management/map-entities/device-map-entity";
import { doesDeviceVersionMatch, GENERATION_CODE } from "../../versions-code";
import { SIGNAL_LEVEL, SIGNAL_LEVEL_THRESHOLDS, UNINITIALIZED_PACKET_LOSS_CEILING } from "./definitions";

const getReferenceTime = (model: DeviceMapEntity["model"]): Date | undefined =>
    model.lastRefreshed instanceof Date && !Number.isNaN(model.lastRefreshed.getTime())
        ? model.lastRefreshed
        : undefined;

/**
 * Resolves online status via the schema-level `getOnlineStatus` utility for the device's `schemaKey`.
 */
export const getDeviceOnlineStatus = ({ model }: { model: DeviceMapEntity["model"] }): ONLINE_STATUS => {
    // Cast satisfies TypeScript; runtime dispatch still uses the document's actual schemaKey.
    const device = model._lastJSON as Device;
    return findUtilities(device).getOnlineStatus(device, getReferenceTime(model));
};

/**
 * Worst-case response time in ms from the device's configured LoRa network profile.
 */
export const getWorstCaseResponseTimeMs = ({ model }: { model: DeviceMapEntity["model"] }): number | undefined => {
    const config = model?._lastJSON?.loraNetworkProfileConfig;
    const effectiveProfile = config ? LoraNetworkProfileConfig.getters.getEffectiveSettings(config) : undefined;
    if (!effectiveProfile) {
        return undefined;
    }
    const worstCaseMs = LoraNetworkProfile.utilities.getWorstCaseResponseTime(effectiveProfile, true);
    return Number.isNaN(worstCaseMs) ? undefined : worstCaseMs;
};

/**
 * Get packet loss data from model
 */
const getPackLossData = ({ model }: { model: DeviceMapEntity["model"] }) => {
    if (!model?.deviceVersionInfo) {
        return handleNonVersionedDevices(model);
    }

    const isSproutDevice = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.SPROUT });
    const isGen4Device = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.GEN4 });

    if (isGen4Device && model?.gen4NetworkInfo) {
        return {
            packetLossUp: model.gen4NetworkInfo?.uplinkPacketLoss,
            packetLossDown: model.gen4NetworkInfo?.downlinkPacketLoss,
        };
    }
    if (isSproutDevice && model?.sproutNetworkInfo?.uplinkPacketLoss !== undefined) {
        return {
            packetLossUp: model.sproutNetworkInfo?.uplinkPacketLoss,
            packetLossDown: model.sproutNetworkInfo?.downlinkPacketLoss,
        };
    }

    return {
        packetLossUp: undefined,
        packetLossDown: undefined,
    };
};

const handleNonVersionedDevices = (model: DeviceMapEntity["model"]) => {
    if (model?.category === "SensoterraMoisture") {
        return {
            packetLossUp: model.device_stats?.uplinkPacketLossRatio,
            packetLossDown: model.device_stats?.downlinkPacketLossRatio,
        };
    }

    return {
        packetLossUp: undefined,
        packetLossDown: undefined,
    };
};
/**
 * Get last contact Date (and time) from model
 */
export const getLastContactDate = ({ model }: { model: DeviceMapEntity["model"] }): Date | null => {
    if (model._lastJSON?.lastContact) {
        const hopefulDate = new Date(model._lastJSON.lastContact);
        if (Number.isNaN(hopefulDate?.valueOf())) {
            console.warn(`Last contact ${model._lastJSON.lastContact} not a date`);
        } else {
            return hopefulDate;
        }
    }

    return null;
};

/**
 * Returns a signal level from a device
 */
export const getSignalLevel = ({ model }: { model: DeviceMapEntity["model"] }): SIGNAL_LEVEL => {
    const onlineStatus = getDeviceOnlineStatus({ model });
    if (onlineStatus === ONLINE_STATUS.Offline) {
        return SIGNAL_LEVEL.ERROR;
    }
    if (onlineStatus === ONLINE_STATUS.AwaitingConnection) {
        return SIGNAL_LEVEL.UNKNOWN;
    }

    const { packetLossUp, packetLossDown } = getPackLossData({ model });

    if (!isValidNum(packetLossUp) || !isValidNum(packetLossDown)) {
        return SIGNAL_LEVEL.UNKNOWN;
    }

    let totalPacketLoss = (packetLossUp + packetLossDown) / 2;
    if (packetLossDown > 0.4 || (packetLossDown === 0 && packetLossUp > 0.2)) {
        totalPacketLoss = Math.max(packetLossDown, packetLossUp);
    }

    if (!isValidNum(totalPacketLoss) || totalPacketLoss <= UNINITIALIZED_PACKET_LOSS_CEILING) {
        return SIGNAL_LEVEL.UNKNOWN;
    }

    if (totalPacketLoss > SIGNAL_LEVEL_THRESHOLDS[SIGNAL_LEVEL.VERY_LOW]) {
        return SIGNAL_LEVEL.VERY_LOW;
    }
    if (totalPacketLoss > SIGNAL_LEVEL_THRESHOLDS[SIGNAL_LEVEL.LOW]) {
        return SIGNAL_LEVEL.LOW;
    }
    if (totalPacketLoss > SIGNAL_LEVEL_THRESHOLDS[SIGNAL_LEVEL.OK]) {
        return SIGNAL_LEVEL.OK;
    }
    if (totalPacketLoss > SIGNAL_LEVEL_THRESHOLDS[SIGNAL_LEVEL.GOOD]) {
        return SIGNAL_LEVEL.GOOD;
    }
    return SIGNAL_LEVEL.EXCELLENT;
};

/**
 * Returns the last network test period for a device
 * @deprecated Use `Device.getters.getNetworkTestPeriod(model)` directly.
 */
export function getLastNetworkTestPeriod({ model }: { model: DeviceMapEntity["model"] }): NetworkTestPeriod {
    // the device getter can only handle device with this specific field. We need to migrate this so that
    // we are doing this based on schema type, which I just learned isn't being transmitted to the front.
    if (!model._lastJSON.DeviceType) {
        return {
            lastNetworkTestEnd: null,
            lastNetworkTestStart: null,
            currentlyConductingNetworkTest: false,
        };
    }
    try {
        return Device.getters.getNetworkTestPeriod(model._lastJSON);
    } catch (e) {
        console.error(e);
        console.error("Error in trying to get network test period, model is", model);
    }
    return {
        lastNetworkTestEnd: null,
        lastNetworkTestStart: null,
        currentlyConductingNetworkTest: false,
    };
}

/**
 * Returns the uplink period for a device
 */
export function getUplinkPeriod({ model }: { model: DeviceMapEntity["model"] }) {
    const isGen4Device = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.GEN4 });
    const isSproutDevice = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.SPROUT });

    if (isGen4Device && model.gen4NetworkInfo) {
        return model.gen4NetworkInfo.currentStandardUplinkPeriod;
    }
    if (isSproutDevice && model.sproutNetworkInfo) {
        return model.sproutNetworkInfo.currentStandardUplinkPeriod;
    }
    return undefined;
}
